/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useState} from 'react'
import {Dispatch} from 'redux'
import {Modal} from 'react-bootstrap'
import './dialog.scss'
import {CustomOtpInput} from '../../app/modules/auth/components/otpinput/CustomOtpInput'
import {
  formattedPhoneNumber,
  handleNotificationWithToastify,
  maskEmail,
  maskPhoneNumber,
  validateEmail,
  validatePhoneNumber,
} from '../../app/services/Functional'
import {ResendOTP} from '../../app/modules/auth/components/resendotp/ResendOtp'
import PhoneInput from 'react-phone-input-2'
import {useNavigate} from 'react-router-dom'
import * as auth_actions from '../../setup/redux/auth/actions'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../setup'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import FAIcon from '../helpers/components/FAIcon'
import {faEnvelope} from '@fortawesome/free-solid-svg-icons'
import {postRequest} from '../../setup/redux/common/cruds'

interface Payload {
  user_id?: string | number
  mobile_no?: string
  email?: string
  mode: 'mobile' | 'email'
}

const API_URL = process.env.REACT_APP_API_BASE_URL

const FieldEditDialog: FC<{
  isOpenDialog: boolean
  mode: string
  handleClose: () => void
  handleverifiedOTP: () => void
  onHideDialog?: () => void
  stepStartingNumber: number
  newEmail?: string
  newPhoneNumber?: string
  newCountryCode?: string
  signUpResendOTPPayload?: any
  signUpVerifyOTPPayload?: any
}> = ({
  isOpenDialog,
  mode,
  handleClose,
  handleverifiedOTP,
  onHideDialog,
  stepStartingNumber,
  newPhoneNumber,
  newCountryCode,
  newEmail,
  signUpResendOTPPayload,
  signUpVerifyOTPPayload,
}) => {
  const {actionsLoading, user}: any = useSelector<RootState>(
    (state) => ({
      actionsLoading: state.common.actionsLoading,
      user: state.auth.user,
    }),
    shallowEqual
  )

  const dispatch: Dispatch<any> = useDispatch()
  let navigate = useNavigate()
  const isMobileMode = mode === 'mobile'
  const isDualOtpPhoneChange = isMobileMode && stepStartingNumber === 0
  const [step, setStep] = useState(stepStartingNumber)
  const [changedEmail, setChangedEmail] = useState(() => {
    return stepStartingNumber === 1 && newEmail ? newEmail : ''
  })
  const [changedCountryCode, setChangedCountryCode] = useState(() => {
    return stepStartingNumber === 1 && newCountryCode ? newCountryCode : ''
  })
  const [changedPhoneNumber, setChangedPhoneNumber] = useState(() => {
    return stepStartingNumber === 1 && newPhoneNumber ? newPhoneNumber : ''
  })
  const [otp, setOtp] = useState('')
  const [emailOtpValue, setEmailOtpValue] = useState('')
  const [phoneOtpValue, setPhoneOtpValue] = useState('')
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [dualOtpLoading, setDualOtpLoading] = useState(false)
  // step 1 = enter email OTP, step 2 = enter phone OTP, step 3 = success (for isDualOtpPhoneChange)

  const handleEmailChange = (event: any) => {
    setChangedEmail(event.target.value)
  }
  const handleOTPChange = (otp: string) => {
    setOtp(otp)
  }

  const extractErrorMessage = (e: any, fallback: string): string => {
    const d = e?.response?.data
    if (typeof d?.message === 'string') return d.message
    if (Array.isArray(d?.message)) return d.message.filter(Boolean).join(' ')
    if (typeof d === 'string') return d
    if (typeof d?.error === 'string') return d.error
    return fallback
  }

  const handleGenerateOTP = async () => {
    if (isDualOtpPhoneChange) {
      setDualOtpLoading(true)
      try {
        const resp = await postRequest(`${API_URL}auth/me/phone/initiate`, {
          country_code: changedCountryCode,
          phone: changedPhoneNumber,
        } as any)
        if (resp?.data?.success) {
          handleNotificationWithToastify(resp.data)
          setStep(1)
        } else {
          handleNotificationWithToastify(resp.data)
        }
      } catch (e: any) {
        handleNotificationWithToastify({
          success: false,
          code: e?.response?.data?.code,
          message: extractErrorMessage(e, 'Failed to initiate phone change'),
        })
      } finally {
        setDualOtpLoading(false)
      }
      return
    }

    let values
    if (isMobileMode) {
      values = {
        mobile_no: `+${changedCountryCode + changedPhoneNumber}`,
        mode: 'mobile',
        user_id: user?.sub || user?.id,
      }
    } else {
      values = {
        email: changedEmail,
        mode: 'email',
        user_id: user?.sub || user?.id,
      }
    }
    let response: any = await dispatch(auth_actions.handleOTPMode(values))
    if (!response.success && response.code === 401) {
      navigate('/error/auth')
    } else if (!response.data.success) {
      handleNotificationWithToastify(response.data)
    } else if (response.status === 200 || response.status === 201) {
      setStep(1)
      handleNotificationWithToastify(response.data)
    } else {
      handleNotificationWithToastify(response.data)
    }
  }

  const handleVerifyEmailOtp = async () => {
    if (emailOtpValue.length < 6) return
    setDualOtpLoading(true)
    try {
      const resp = await postRequest(`${API_URL}auth/me/phone/verify-email`, {
        email_otp: emailOtpValue,
      } as any)
      if (resp?.data?.success) {
        setHasErrors(false)
        handleNotificationWithToastify(resp.data)
        setEmailOtpValue('')
        setStep(2)
      } else {
        setHasErrors(true)
        handleNotificationWithToastify(resp.data)
        setTimeout(() => { setHasErrors(false); setEmailOtpValue('') }, 3000)
      }
    } catch (e: any) {
      setHasErrors(true)
      handleNotificationWithToastify({
        success: false,
        code: e?.response?.data?.code,
        message: extractErrorMessage(e, 'Email verification failed'),
      })
      setTimeout(() => { setHasErrors(false); setEmailOtpValue('') }, 3000)
    } finally {
      setDualOtpLoading(false)
    }
  }

  const handleVerifyPhoneOtp = async () => {
    if (phoneOtpValue.length < 6) return
    setDualOtpLoading(true)
    try {
      const resp = await postRequest(`${API_URL}auth/me/phone/confirm`, {
        phone_otp: phoneOtpValue,
      } as any)
      if (resp?.data?.success) {
        setHasErrors(false)
        handleNotificationWithToastify(resp.data)
        handleverifiedOTP()
        setStep(3)
        setTimeout(() => { handleClose() }, 5000)
      } else {
        setHasErrors(true)
        handleNotificationWithToastify(resp.data)
        setTimeout(() => { setHasErrors(false); setPhoneOtpValue('') }, 3000)
      }
    } catch (e: any) {
      setHasErrors(true)
      handleNotificationWithToastify({
        success: false,
        code: e?.response?.data?.code,
        message: extractErrorMessage(e, 'Phone verification failed'),
      })
      setTimeout(() => { setHasErrors(false); setPhoneOtpValue('') }, 3000)
    } finally {
      setDualOtpLoading(false)
    }
  }

  const handleOTPSubmit = async () => {
    if (otp.length === 6) {
      let response: any
      if (stepStartingNumber === 1) {
        response = await dispatch(
          auth_actions.handleVerifyOTP({
            ...signUpVerifyOTPPayload,
            otp: otp,
          })
        )
      } else {
        response = await dispatch(
          auth_actions.verifyOTP({
            id: user?.sub || user?.id,
            otp: otp,
            [isMobileMode ? 'new_mobile_no' : 'new_email']: isMobileMode
              ? changedPhoneNumber
              : changedEmail,
            ...(isMobileMode && {new_country_code: changedCountryCode}),
          })
        )
      }
      if (response?.success) {
        setHasErrors(false)
        localStorage.setItem('otpRemainingTime', '60')
        localStorage.setItem('otpResendCount', '0')
        localStorage.setItem('otpMaxResendCount', '3')
        handleverifiedOTP()
        setStep(2)
        setTimeout(() => {
          handleClose()
        }, 5000)
      } else {
        setHasErrors(true)
        handleNotificationWithToastify(response)
        setTimeout(() => {
          setHasErrors(false)
          setOtp('')
        }, 5000)
      }
    }
  }

  let payload: Payload

  if (isMobileMode) {
    payload = {
      mobile_no: formattedPhoneNumber(changedCountryCode, changedPhoneNumber),
      mode: 'mobile',
      user_id: user?.sub || user?.id,
    }
  } else {
    payload = {
      email: changedEmail,
      mode: 'email',
      user_id: user?.sub || user?.id,
    }
  }

  return (
    <Modal
      show={isOpenDialog}
      onHide={stepStartingNumber === 1 ? onHideDialog : handleClose}
      className='custom-dialog'
      size='lg'
      centered
      tabIndex={0}
      backdrop='static'
    >
      <Modal.Header className='border-0 pb-0' closeButton></Modal.Header>
      <Modal.Body className='pb-12 pt-0'>
        <Modal.Title className='fs-1 text-center custom-dialog-title pb-6'>
          {stepStartingNumber === 0 ? 'Change' : 'Verify'} {isMobileMode ? 'Phone Number' : 'Email'}
        </Modal.Title>
        {step === 0 && (
          <div className='custom-dialog-container pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
            <div className='custom-dialog-sub-header'>
              Provide your new {isMobileMode ? 'phone number' : 'email'} that you want to change.
            </div>
            {isMobileMode ? (
              <div className='m-0 phone-field form-group custom-dialog-field'>
                <PhoneInput
                  buttonClass='flag_dropdown'
                  dropdownStyle={{backgroundColor: 'none'}}
                  enableSearch={true}
                  disableSearchIcon={true}
                  searchStyle={{width: '90%', margin: '0'}}
                  countryCodeEditable={false}
                  country='gr'
                  value={''}
                  inputProps={{
                    name: 'phone',
                    id: 'phone',
                    required: true,
                  }}
                  onChange={(phone: any, data: any) => {
                    setChangedCountryCode(data.dialCode)
                    setChangedPhoneNumber(phone.slice(data.dialCode.length))
                  }}
                />
              </div>
            ) : (
              <div className='form-group m-0 custom-dialog-field'>
                <input
                  type='text'
                  tabIndex={1}
                  placeholder='New Email ID'
                  autoComplete='off'
                  className='form-control dialog_input'
                  value={changedEmail}
                  onChange={handleEmailChange}
                />
                <FAIcon
                  icon={faEnvelope}
                  className='custom-dialog-input-icon'
                  defaultClassnameApply={false}
                />
              </div>
            )}
            <button
              type='submit'
              disabled={
                isMobileMode
                  ? validatePhoneNumber(changedPhoneNumber, changedCountryCode) || dualOtpLoading
                  : validateEmail(changedEmail) || actionsLoading
              }
              tabIndex={1}
              onClick={handleGenerateOTP}
              className={`btn btn-primary btn-pill w-100 dialog-submit-button`}
            >
              {dualOtpLoading ? 'Sending...' : isDualOtpPhoneChange ? 'Send OTP to Email' : 'Generate OTP'}
              <img
                className='ms-3'
                src={'/media/icons/duotune/general/Arrow - Right.svg'}
                alt='->'
              />
            </button>
          </div>
        )}
        {step === 1 && isDualOtpPhoneChange && (
          <div className='custom-dialog-container pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
            <div className='otp_sub_header dialog-otp-sub-header text-center'>
              Enter the 6-digit code sent to your primary email address.
            </div>
            <div className='d-flex justify-content-center w-100'>
              <CustomOtpInput
                label='email-otp'
                otp={emailOtpValue}
                error={hasErrors}
                handleOTPChange={(val: string) => setEmailOtpValue(val)}
                className='dialog-otp-input'
                separatorClassName='dialog-otp-separator'
              />
            </div>
            <button
              type='submit'
              disabled={emailOtpValue.length < 6 || dualOtpLoading}
              tabIndex={1}
              onClick={handleVerifyEmailOtp}
              className={`btn btn-primary btn-pill w-100 dialog-submit-button`}
            >
              {dualOtpLoading ? 'Verifying...' : 'Verify Email Code'}
              <img
                className='ms-3'
                src={'/media/icons/duotune/general/Arrow - Right.svg'}
                alt='->'
              />
            </button>
          </div>
        )}
        {step === 2 && isDualOtpPhoneChange && (
          <div className='custom-dialog-container pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
            <div className='otp_sub_header dialog-otp-sub-header text-center'>
              Enter the 6-digit code sent to your new phone number.
            </div>
            <div className='d-flex justify-content-center w-100'>
              <CustomOtpInput
                label='phone-otp'
                otp={phoneOtpValue}
                error={hasErrors}
                handleOTPChange={(val: string) => setPhoneOtpValue(val)}
                className='dialog-otp-input'
                separatorClassName='dialog-otp-separator'
              />
            </div>
            <button
              type='submit'
              disabled={phoneOtpValue.length < 6 || dualOtpLoading}
              tabIndex={1}
              onClick={handleVerifyPhoneOtp}
              className={`btn btn-primary btn-pill w-100 dialog-submit-button`}
            >
              {dualOtpLoading ? 'Verifying...' : 'Verify Phone Code'}
              <img
                className='ms-3'
                src={'/media/icons/duotune/general/Arrow - Right.svg'}
                alt='->'
              />
            </button>
          </div>
        )}
        {step === 1 && !isDualOtpPhoneChange && (
          <div className='custom-dialog-container pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
            <div className='otp_sub_header dialog-otp-sub-header'>
              <div className=''>
                Enter the 6-digit code sent to
              </div>
              <OverlayTrigger
                key='show'
                placement='top'
                overlay={
                  <Tooltip id='show'>
                    {isMobileMode
                      ? maskPhoneNumber(changedPhoneNumber, changedCountryCode)
                      : maskEmail(changedEmail)}
                  </Tooltip>
                }
              >
                <span className='mask-text'>
                  {' '}
                  {isMobileMode
                    ? maskPhoneNumber(changedPhoneNumber, changedCountryCode)
                    : maskEmail(changedEmail)}
                </span>
              </OverlayTrigger>
            </div>
            <CustomOtpInput
              label='otp'
              otp={otp}
              error={hasErrors}
              handleOTPChange={handleOTPChange}
              className='dialog-otp-input'
              separatorClassName='dialog-otp-separator'
            />
            <ResendOTP
              className='dialog-resend-otp'
              APICalled={
                stepStartingNumber === 1
                  ? auth_actions.resendOTP(signUpResendOTPPayload)
                  : auth_actions.handleOTPMode(payload)
              }
              showBackBtn={false}
            />
            <button
              type='submit'
              disabled={otp.length < 6 || actionsLoading}
              tabIndex={1}
              onClick={handleOTPSubmit}
              className={`btn btn-primary btn-pill w-100 dialog-submit-button`}
            >
              Verify
              <img
                className='ms-3'
                src={'/media/icons/duotune/general/Arrow - Right.svg'}
                alt='->'
              />
            </button>
          </div>
        )}
        {((isDualOtpPhoneChange && step === 3) || (!isDualOtpPhoneChange && step === 2)) && (
          <div className='custom-dialog-container pt-6 d-flex flex-column justify-content-center align-items-center gap-6'>
            <img className='verified' src={'/media/verified.gif'} alt='Verified' />
            <div className='custom-dialog-sub-header'>
              Your {isMobileMode ? 'Phone Number' : 'Email ID'} Change Successfully !
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export {FieldEditDialog}
